import React, { useState, useEffect } from 'react';
import './RecipeLibrary.css';

const API_URL = 'https://api.productfootprint.awstommy.com';

interface Ingredient {
  id: string;
  name: string;
  category: string;
  imageUrl: string;
}

interface RecipeIngredient {
  ingredientId: string;
  quantity: number;
}

interface Recipe {
  id: string;
  name: string;
  ingredients: RecipeIngredient[];
  imageUrl?: string;
  totalEmissions?: number;
  createdAt: string;
}

interface RecipeLibraryProps {
  readOnly?: boolean;
}

export default function RecipeLibrary({ readOnly = false }: RecipeLibraryProps) {
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [recipeName, setRecipeName] = useState('');
  const [recipeImage, setRecipeImage] = useState<string>('');
  const [selectedIngredients, setSelectedIngredients] = useState<RecipeIngredient[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [editingRecipeId, setEditingRecipeId] = useState<string>('');
  const [showForm, setShowForm] = useState(false);
  const [showCloneOptions, setShowCloneOptions] = useState(false);

  useEffect(() => {
    loadRecipes();
    loadIngredients();
  }, []);

  const loadRecipes = async () => {
    try {
      setIsLoading(true);
      console.log('Loading recipes...');
      const response = await fetch(`${API_URL}/recipe-storage`);
      console.log('Recipe response status:', response.status);
      if (!response.ok) throw new Error('Failed to load recipes');
      const data = await response.json();
      console.log('Loaded recipes:', data.length);
      setRecipes(data);
    } catch (error) {
      console.error('Error loading recipes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadIngredients = async () => {
    try {
      const response = await fetch(`${API_URL}/ingredient-storage`);
      if (!response.ok) throw new Error('Failed to load ingredients');
      const data = await response.json();
      setIngredients(data);
    } catch (error) {
      console.error('Error loading ingredients:', error);
    }
  };

  const addIngredient = (ingredientId: string) => {
    if (!selectedIngredients.find(i => i.ingredientId === ingredientId)) {
      setSelectedIngredients([...selectedIngredients, { ingredientId, quantity: 0 }]);
    }
  };

  const updateQuantity = (ingredientId: string, quantity: number) => {
    setSelectedIngredients(selectedIngredients.map(i => 
      i.ingredientId === ingredientId ? { ...i, quantity } : i
    ));
  };

  const removeIngredient = (ingredientId: string) => {
    setSelectedIngredients(selectedIngredients.filter(i => i.ingredientId !== ingredientId));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!recipeName || selectedIngredients.length === 0) return;

    setIsSaving(true);
    try {
      const payload: any = { name: recipeName, ingredients: selectedIngredients };
      if (editingRecipeId) payload.id = editingRecipeId;
      if (recipeImage && recipeImage.startsWith('data:')) payload.image = recipeImage;
      
      const response = await fetch(`${API_URL}/recipe-storage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save recipe: ${errorText}`);
      }

      setRecipeName('');
      setRecipeImage('');
      setSelectedIngredients([]);
      setEditingRecipeId('');
      setShowForm(false);
      loadRecipes();
    } catch (error) {
      console.error('Error saving recipe:', error);
      alert(`Failed to save recipe: ${error}`);
    } finally {
      setIsSaving(false);
    }
  };

  const deleteRecipe = async (id: string, name: string) => {
    if (!window.confirm(`Delete recipe "${name}"?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/recipe-storage?id=${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete recipe');
      await loadRecipes();
    } catch (error) {
      console.error('Error deleting recipe:', error);
    }
  };

  const getIngredient = (id: string) => ingredients.find(i => i.id === id);

  const editRecipe = (recipe: Recipe) => {
    setEditingRecipeId(recipe.id);
    setRecipeName(recipe.name);
    setRecipeImage(recipe.imageUrl || '');
    setSelectedIngredients(recipe.ingredients);
    setShowForm(true);
  };

  const addNewRecipe = () => {
    if (recipes.length > 0) {
      setShowCloneOptions(true);
    } else {
      startFreshRecipe();
    }
  };

  const startFreshRecipe = () => {
    setEditingRecipeId('');
    setRecipeName('');
    setRecipeImage('');
    setSelectedIngredients([]);
    setShowCloneOptions(false);
    setShowForm(true);
  };

  const cloneRecipe = (recipe: Recipe) => {
    setEditingRecipeId('');
    setRecipeName(recipe.name + ' (Copy)');
    setRecipeImage('');
    setSelectedIngredients([...recipe.ingredients]);
    setShowCloneOptions(false);
    setShowForm(true);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width * 0.25;
          canvas.height = img.height * 0.25;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
          setRecipeImage(canvas.toDataURL('image/jpeg', 0.8));
        };
        img.src = reader.result as string;
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="recipe-library">
      <h2>Recipe Library</h2>
      
      <div className="recipe-list">
        <div className="recipe-list-header">
          <h3>Saved Recipes ({recipes.length})</h3>
          {!readOnly && (
            <button onClick={addNewRecipe} className="btn-add-recipe">+ Add Recipe</button>
          )}
        </div>
        {isLoading ? (
          <p>Loading recipes...</p>
        ) : recipes.length === 0 ? (
          <p>No recipes saved yet. Click "Add Recipe" to create your first one!</p>
        ) : null}
        <div className="recipe-grid">
          {recipes.map(recipe => (
            <div key={recipe.id} className="recipe-card" onClick={!readOnly ? () => editRecipe(recipe) : undefined}>
              {recipe.imageUrl && <img src={recipe.imageUrl} alt={recipe.name} className="recipe-image" />}
              <h4>{recipe.name}</h4>
              {recipe.totalEmissions !== undefined && (
                <div className="recipe-emissions">
                  🌍 {recipe.totalEmissions.toFixed(3)} kg CO₂e
                </div>
              )}
              <div className="recipe-ingredients">
                {recipe.ingredients.map(ri => {
                  const ing = getIngredient(ri.ingredientId);
                  return ing ? (
                    <div key={ri.ingredientId} className="recipe-ingredient">
                      <img src={ing.imageUrl} alt={ing.name} />
                      <span>{ri.quantity}g {ing.name}</span>
                    </div>
                  ) : null;
                })}
              </div>
              {!readOnly && (
                <button onClick={(e) => { e.stopPropagation(); deleteRecipe(recipe.id, recipe.name); }} className="btn-delete">Delete</button>
              )}
            </div>
          ))}
        </div>
      </div>

      {showCloneOptions && (
        <div className="clone-options">
          <h3>Start New Recipe</h3>
          <button onClick={startFreshRecipe} className="btn-start-fresh">Start from Scratch</button>
          <h4>Or Clone Existing Recipe:</h4>
          <div className="clone-grid">
            {recipes.map(recipe => (
              <div key={recipe.id} className="clone-card" onClick={() => cloneRecipe(recipe)}>
                {recipe.imageUrl && <img src={recipe.imageUrl} alt={recipe.name} />}
                <span>{recipe.name}</span>
              </div>
            ))}
          </div>
          <button onClick={() => setShowCloneOptions(false)} className="btn-cancel">Cancel</button>
        </div>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="recipe-form">
        <div className="recipe-form-header">
          <input
            type="text"
            placeholder="Recipe name"
            value={recipeName}
            onChange={(e) => setRecipeName(e.target.value)}
          />
          
          <div className="image-upload">
            <label>Recipe Photo:</label>
            <input type="file" accept="image/*" onChange={handleImageUpload} />
            {recipeImage && <img src={recipeImage} alt="Preview" className="image-preview" />}
          </div>
        </div>

        <div className="recipe-builder">
          <div className="ingredient-selector">
            <h3>Available Ingredients</h3>
            <div className="ingredient-grid">
              {ingredients.map(ing => (
                <div key={ing.id} className="ingredient-option" onClick={() => addIngredient(ing.id)}>
                  <img src={ing.imageUrl} alt={ing.name} />
                  <span>{ing.name}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="selected-ingredients">
            <h3>Recipe Ingredients</h3>
            {selectedIngredients.length === 0 && <p>Click ingredients to add them</p>}
            {selectedIngredients.map(si => {
              const ing = getIngredient(si.ingredientId);
              return ing ? (
                <div key={si.ingredientId} className="selected-ingredient">
                  <img src={ing.imageUrl} alt={ing.name} />
                  <span>{ing.name}</span>
                  <input
                    type="number"
                    value={si.quantity}
                    onChange={(e) => updateQuantity(si.ingredientId, Number(e.target.value))}
                    min="0"
                    step="1"
                    placeholder="grams"
                  />
                  <span className="unit">g</span>
                  <button type="button" onClick={() => removeIngredient(si.ingredientId)}>×</button>
                </div>
              ) : null;
            })}
          </div>
        </div>

        <button type="submit" disabled={isSaving || !recipeName || selectedIngredients.length === 0}>
          {isSaving ? 'Saving...' : editingRecipeId ? 'Update Recipe' : 'Save Recipe'}
        </button>
      </form>
      )}
    </div>
  );
}
